import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './BookAppointment.css';

const BookAppointment = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    yogaClass: '',
    trainer: '',
    experience: '',
    sessionType: '',
    date: '',
    time: '',
    mode: 'Studio',
    message: ''
  });

  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitted(true);
  };

  return (
    <section className="book-appointment">
      <div className="appointment-container">
        
        <div className="appointment-header">
          <h4 className="appointment-subtitle">BOOK APPOINTMENT</h4>
          <h2 className="appointment-title">Begin Your Yoga Journey Today</h2>
          <p className="appointment-desc">
            Choose your preferred class, trainer, and schedule. We'll help you begin your wellness journey.
          </p>
          <div className="appointment-divider"></div>
        </div>

        <div className="appointment-grid">
          {/* Left Side Content */}
          <motion.div 
            className="appointment-left"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6 }}
          >
            <div className="appointment-image-card">
              <img src="https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?auto=format&fit=crop&q=80&w=800" alt="Yoga Journey" className="appointment-img" />
              <div className="appointment-overlay">
                <span className="small-badge">START YOUR JOURNEY</span>
                <h3 className="left-title">Every Breath Brings You Closer to Balance</h3>
                <p className="left-desc">
                  Join our peaceful yoga community and experience personalized guidance, mindful movement, and holistic wellness.
                </p>
                
                <ul className="appointment-features">
                  <li><span className="check">✓</span> Certified Yoga Trainers</li>
                  <li><span className="check">✓</span> Flexible Class Timings</li>
                  <li><span className="check">✓</span> Beginner Friendly</li>
                  <li><span className="check">✓</span> Online & Offline Sessions</li>
                </ul>
              </div>
            </div>
          </motion.div>

          {/* Right Side Form */}
          <motion.div 
            className="appointment-right"
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <AnimatePresence mode="wait">
              {isSubmitted ? (
                <motion.div 
                  className="success-message"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                >
                  <div className="check-circle">✓</div>
                  <h3>Appointment Request Submitted</h3>
                  <p>Thank you for choosing our yoga studio.</p>
                  <p>Our team will contact you shortly to confirm your booking.</p>
                  <button onClick={() => setIsSubmitted(false)} className="btn-reset">Book Another Session</button>
                </motion.div>
              ) : (
                <motion.form 
                  className="appointment-form"
                  onSubmit={handleSubmit}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <div className="form-group">
                    <label>Full Name</label>
                    <input type="text" name="name" placeholder="Enter your full name" required onChange={handleChange} />
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label>Email Address</label>
                      <input type="email" name="email" placeholder="Enter your email" required onChange={handleChange} />
                    </div>
                    <div className="form-group">
                      <label>Phone Number</label>
                      <input type="tel" name="phone" placeholder="Enter your mobile number" required onChange={handleChange} />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Select Yoga Class</label>
                      <div className="select-wrapper">
                        <select name="yogaClass" required onChange={handleChange} defaultValue="">
                          <option value="" disabled>Select Yoga Class</option>
                          <option value="Hatha Yoga">Hatha Yoga</option>
                          <option value="Vinyasa Flow">Vinyasa Flow</option>
                          <option value="Ashtanga Yoga">Ashtanga Yoga</option>
                          <option value="Yin Yoga">Yin Yoga</option>
                          <option value="Power Yoga">Power Yoga</option>
                          <option value="Meditation">Meditation</option>
                          <option value="Kids Yoga">Kids Yoga</option>
                          <option value="Private Session">Private Sessions</option>
                        </select>
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Preferred Trainer</label>
                      <div className="select-wrapper">
                        <select name="trainer" onChange={handleChange} defaultValue="">
                          <option value="" disabled>Select Trainer</option>
                          <option value="Sarah Johnson">Sarah Johnson</option>
                          <option value="Emily Wilson">Emily Wilson</option>
                          <option value="Olivia Davis">Olivia Davis</option>
                          <option value="Michael Brown">Michael Brown</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Experience Level</label>
                      <div className="select-wrapper">
                        <select name="experience" onChange={handleChange} defaultValue="">
                          <option value="" disabled>Select Level</option>
                          <option value="Beginner">Beginner</option>
                          <option value="Intermediate">Intermediate</option>
                          <option value="Advanced">Advanced</option>
                        </select>
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Session Type</label>
                      <div className="select-wrapper">
                        <select name="sessionType" onChange={handleChange} defaultValue="">
                          <option value="" disabled>Select Session</option>
                          <option value="Studio Class">Studio Class</option>
                          <option value="Online Class">Online Class</option>
                          <option value="Private Session">Private Session</option>
                          <option value="Group Session">Group Session</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Preferred Date</label>
                      <input type="date" name="date" required onChange={handleChange} />
                    </div>
                    <div className="form-group">
                      <label>Preferred Time</label>
                      <div className="select-wrapper">
                        <select name="time" required onChange={handleChange} defaultValue="">
                          <option value="" disabled>Select Time</option>
                          <option value="06:00 AM">06:00 AM</option>
                          <option value="07:30 AM">07:30 AM</option>
                          <option value="09:00 AM">09:00 AM</option>
                          <option value="05:00 PM">05:00 PM</option>
                          <option value="06:30 PM">06:30 PM</option>
                          <option value="08:00 PM">08:00 PM</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="form-group mode-selection">
                    <label>Mode</label>
                    <div className="radio-group">
                      <label className="radio-label">
                        <input type="radio" name="mode" value="Studio" defaultChecked onChange={handleChange} /> Studio
                      </label>
                      <label className="radio-label">
                        <input type="radio" name="mode" value="Online" onChange={handleChange} /> Online
                      </label>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Message</label>
                    <textarea name="message" rows="3" placeholder="Tell us about your wellness goals..." onChange={handleChange}></textarea>
                  </div>

                  <button type="submit" className="btn-submit">Book Appointment</button>
                </motion.form>
              )}
            </AnimatePresence>
          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default BookAppointment;
